<template>
  <v-alert v-if="!arenaId" type="info" dense class="mb-4">
    {{ $t('entities.invoices.selectArenaReminderForTransactions') }}
  </v-alert>
  <div v-else>
    <div class="d-flex align-center justify-end">
      <CatalogSwitch
        :route="{ name: 'navigationDrawer.shop.invoices' }"
        :initial-value="$t('entities.invoices.transactions')"
        :options="[
          $t('entities.invoices.title'),
          $t('entities.invoices.transactions'),
        ]" />
    </div>
    <v-card>
      <v-row class="px-4 pt-4 mb-4" align="center">
        <v-col cols="12" sm="6" md="4">
          <v-select
            v-model="pagination.size"
            :items="rowsPerPageItems"
            :menu-props="{ bottom: true, offsetY: true }"
            :no-data-text="$t('components.noData.noEntriesFound')"
            :disabled="!rowsPerPageItems.length || isLoadingTransactions"
            hide-details="auto"
            solo>
            <template v-slot:label>
              {{ $t('components.rowsPerPage.entries') }}
            </template>
            <template v-slot:selection="data">
              {{ $t('components.rowsPerPage.entries') }}: {{ data.item }}
            </template>
          </v-select>
        </v-col>
        <v-col cols="12" sm="6" md="4">
          <v-text-field
            v-model="filters.created_gte"
            type="date"
            solo
            hide-details="auto"
            clearable>
            <template v-slot:prepend-inner>
              {{ $t('miscellaneous.dateFrom') }}:
            </template>
          </v-text-field>
        </v-col>
        <v-col cols="12" sm="6" md="4">
          <v-text-field
            v-model="filters.created_lt"
            type="date"
            solo
            hide-details="auto"
            clearable>
            <template v-slot:prepend-inner>
              {{ $t('miscellaneous.dateTo') }}:
            </template>
          </v-text-field>
        </v-col>
        <v-col cols="12" sm="6" md="4">
          <v-select
            v-model="filters.sort"
            :items="sortOptions"
            :menu-props="{ bottom: true, offsetY: true }"
            :disabled="isLoadingTransactions"
            hide-details="auto"
            item-text="text"
            item-value="value"
            solo>
            <template v-slot:label>
              {{ $t('miscellaneous.sortBy') }}
            </template>
            <template v-slot:selection="{ item }">
              {{ item.text }}
            </template>
          </v-select>
        </v-col>
        <v-col cols="12" sm="6" md="4">
          <v-autocomplete
            v-model="filters.buyer_id"
            :items="buyerUsers"
            :loading="loadingBuyerUsers"
            item-text="displayName"
            item-value="id"
            no-filter
            solo
            hide-details="auto"
            :search-input.sync="buyerSearch"
            clearable>
            <template v-slot:prepend-inner>
              {{ $t('entities.invoices.filters.buyer') }}:
            </template>
          </v-autocomplete>
        </v-col>
        <v-col cols="12" sm="6" md="4">
          <v-autocomplete
            v-model="filters.seller_id"
            :items="sellerUsers"
            :loading="loadingSellerUsers"
            item-text="displayName"
            item-value="id"
            no-filter
            solo
            hide-details="auto"
            :search-input.sync="sellerSearch"
            clearable>
            <template v-slot:prepend-inner>
              {{ $t('entities.invoices.filters.seller') }}:
            </template>
          </v-autocomplete>
        </v-col>
      </v-row>
      <v-data-table
        :headers="headers"
        :items="transactions"
        :items-per-page="pagination.size"
        :loading="isLoadingTransactions"
        :loading-text="$t('components.noData.fetchingEntries')"
        hide-default-footer>
        <template v-slot:top>
          <v-toolbar flat>
            <v-toolbar-title>{{
              $t('entities.invoices.transactions')
            }}</v-toolbar-title>
            <v-divider class="mx-4" inset vertical />
            <small class="mr-2">{{ pagination.count }}</small>
            <v-btn
              depressed
              x-small
              dark
              fab
              color="primary"
              :loading="isLoadingTransactions"
              :disabled="isLoadingTransactions"
              @click="refresh">
              <v-icon>mdi-refresh</v-icon>
            </v-btn>
          </v-toolbar>
        </template>
        <template v-slot:item.id="{ item }">
          {{ item.id }}
        </template>
        <template v-slot:item.coins_given="{ item }">
          <span class="font-weight-bold">
            {{ item.coins_given }} <CoinIcon />
          </span>
        </template>
        <template v-slot:item.seller_user_id="{ item }">
          {{ getUserDisplayName(item.seller_user_id) }}
        </template>
        <template v-slot:item.buyer_user_id="{ item }">
          {{ getUserDisplayName(item.buyer_user_id) }}
        </template>
        <template v-slot:item.created_at="{ item }">
          {{ formatDateValue(item.created_at) }}
        </template>
        <template v-slot:item.actions="{ item }">
          <v-tooltip top color="transparent">
            <template v-slot:activator="{ on }">
              <v-btn
                v-on="on"
                :disabled="
                  !!item.restoring_transaction_id ||
                  restoringTransactionId === item.id
                "
                color="error"
                class="ml-2"
                depressed
                x-small
                fab
                @click="openRestoreDialog(item)">
                <template v-if="restoringTransactionId === item.id">
                  <ProgressCircular color="error" />
                </template>
                <template v-else>
                  <v-icon color="white" small>restore</v-icon>
                </template>
              </v-btn>
            </template>
            <v-card class="elevation-2 px-4 py-2">
              <span>{{ $t('entities.invoices.restoreTransaction') }}</span>
            </v-card>
          </v-tooltip>
        </template>
        <template v-slot:no-data>
          <div class="py-6 text-center grey--text">
            {{ $t('components.noData.noEntriesFound') }}
          </div>
        </template>
      </v-data-table>
      <v-layout
        v-if="pagination.count"
        class="text-center mt-3 pb-4"
        justify-center>
        <v-flex>
          <v-pagination
            v-model="pagination.page"
            :length="pagination.last_page"
            :total-visible="$vuetify.breakpoint.mdAndUp ? 10 : 5"
            :disabled="isLoadingTransactions"
            prev-icon="mdi-menu-left"
            next-icon="mdi-menu-right" />
          <h5 class="card-title font-weight-light mt-2">
            {{
              `${pagination.from} - ${pagination.to} ${$t(
                'components.pagination.of',
              )} ${pagination.count}`
            }}
          </h5>
        </v-flex>
      </v-layout>
      <RestoreDialog
        :value="restoreDialogOpen"
        :transaction="transactionToRestore"
        :arena-name="arenaName"
        :seller-user-name="
          getUserDisplayName(transactionToRestore?.seller_user_id)
        "
        :restoring="restoring"
        @input="restoreDialogOpen = $event"
        @restore="handleRestore" />
    </v-card>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import Transaction from '@/models/shop/TransactionV2'
import User from '@/models/users/User'
import formatDate from '@/mixins/format-date'
import RestoreDialog from './RestoreDialog.vue'
import ProgressCircular from '@/components/ProgressCircular.vue'
import CatalogSwitch from '@/components/CatalogSwitch.vue'
import { TRANSACTION_TABLE_HEADERS } from './headers'
import CoinIcon from '@/components/CoinIcon.vue'

const resetPagination = () => ({
  size: 10,
  from: 0,
  to: 0,
  page: 1,
  last_page: 0,
  count: 0,
})

export default {
  name: 'Transactions',
  components: { RestoreDialog, ProgressCircular, CatalogSwitch, CoinIcon },
  mixins: [formatDate],
  data() {
    return {
      filters: {
        sort: '-created_at',
        created_gte: '',
        created_lt: '',
        buyer_id: null,
        seller_id: null,
      },
      buyerUsers: [],
      loadingBuyerUsers: false,
      buyerSearch: '',
      buyerSearchDebounce: null,
      sellerUsers: [],
      loadingSellerUsers: false,
      sellerSearch: '',
      sellerSearchDebounce: null,
      transactions: [],
      isLoadingTransactions: false,
      pagination: resetPagination(),
      usersById: {},
      restoreDialogOpen: false,
      transactionToRestore: null,
      restoring: false,
      restoringTransactionId: null,
    }
  },
  computed: {
    arenaId() {
      return this.$store.getters?.arena?.id
    },
    arenaName() {
      return this.$store.getters?.arena?.name ?? ''
    },
    rowsPerPageItems() {
      return [5, 10, 25, 50]
    },
    headers() {
      return TRANSACTION_TABLE_HEADERS.map((header) => ({
        ...header,
        text: this.$t(header.text),
      }))
    },
    sortOptions() {
      return [
        {
          text: this.$t('entities.products.filters.newest'),
          value: '-created_at',
        },
        {
          text: this.$t('entities.products.filters.oldest'),
          value: 'created_at',
        },
      ]
    },
  },
  created() {
    this.fetchTransactions()
  },
  methods: {
    ...mapActions(['showSnackbar']),
    openRestoreDialog(transaction) {
      this.transactionToRestore = transaction
      this.restoreDialogOpen = true
    },
    async handleRestore(payload) {
      if (!this.arenaId || !this.transactionToRestore?.id) return
      this.restoring = true
      this.restoringTransactionId = this.transactionToRestore.id
      try {
        await Transaction.api.restore(
          this.arenaId,
          this.transactionToRestore.id,
          payload,
        )
        this.showSnackbar({
          color: 'success',
          message: this.$t(
            'entities.invoices.notifications.transactionRestore',
          ),
        })
        this.restoreDialogOpen = false
        this.transactionToRestore = null
        this.refresh()
      } catch (error) {
        const data = error?.response?.data
        let message = data?.message ?? 'Failed to restore transaction.'
        const errors = []
        let iconAndColor = 'error'
        if (error?.response?.status === 422) {
          iconAndColor = 'warning'
          if (data?.errors) {
            for (const i in data.errors) {
              errors.push(`${i}: ${data.errors[i]}`)
            }
            message += ' ' + errors.join(', ')
          }
        }
        if (error?.response?.status === 500 && data?.errors) {
          for (const i in data.errors) {
            errors.push(data.errors[i])
          }
          message += ' ' + errors.join(', ')
        }
        this.showSnackbar({
          color: iconAndColor,
          message,
          timeout: 10000,
          icon: iconAndColor,
        })
        this.restoreDialogOpen = false
        this.transactionToRestore = null
      } finally {
        this.restoring = false
        this.restoringTransactionId = null
      }
    },
    getUserDisplayName(userId) {
      if (!userId) return '/'
      const user = this.usersById[userId]
      if (!user) return '/'
      if (user.identity?.name) return user.identity.name
      if (user?.name) return user?.name
      if (user.username) return user.username
      return '/'
    },
    formatDateValue(value) {
      if (!value) return '-'
      const parsed = new Date(value)
      if (Number.isNaN(parsed.getTime())) return value
      return this.formatDate(parsed, true, true)
    },
    resetState() {
      this.transactions = []
      this.usersById = {}
      this.pagination = resetPagination()
      this.filters.sort = '-created_at'
      this.filters.created_gte = ''
      this.filters.created_lt = ''
      this.filters.buyer_id = null
      this.filters.seller_id = null
      this.buyerUsers = []
      this.buyerSearch = ''
      this.sellerUsers = []
      this.sellerSearch = ''
    },
    async fetchBuyerUsers(query = '') {
      try {
        this.loadingBuyerUsers = true
        const params = this.getBuyerUserRequestParams(query)
        const response = await User.api.get(params)
        const data = response.data?.content || []
        this.buyerUsers = data.map((user) => ({
          ...user,
          displayName: user.identity?.name
            ? `${user.identity.name} (${user.username})`
            : user.username,
        }))
      } catch (error) {
        console.warn('Searching for buyer users failed.', error)
        this.buyerUsers = []
      } finally {
        this.loadingBuyerUsers = false
      }
    },
    getBuyerUserRequestParams(query = '') {
      if (!query) return 'size=10&page=1'
      const searchParams = this.getBuyerSearchParams(query)
      return `size=10&page=1&${searchParams}`
    },
    getBuyerSearchParams(searchTerm = '') {
      if (!searchTerm) return ''
      const searchAttributes = ['username', 'email', 'name', 'card_id', 'phone']
      const searchStrings = searchAttributes.map((it) => `${it}=${searchTerm}`)
      return searchStrings.join('&')
    },
    async fetchSellerUsers(query = '') {
      try {
        this.loadingSellerUsers = true
        const params = this.getSellerUserRequestParams(query)
        const response = await User.api.get(params)
        const data = response.data?.content || []
        this.sellerUsers = data.map((user) => ({
          ...user,
          displayName: user.identity?.name
            ? `${user.identity.name} (${user.username})`
            : user.username,
        }))
      } catch (error) {
        console.warn('Searching for seller users failed.', error)
        this.sellerUsers = []
      } finally {
        this.loadingSellerUsers = false
      }
    },
    getSellerUserRequestParams(query = '') {
      if (!query) return 'size=10&page=1'
      const searchParams = this.getSellerSearchParams(query)
      return `size=10&page=1&${searchParams}`
    },
    getSellerSearchParams(searchTerm = '') {
      if (!searchTerm) return ''
      const searchAttributes = ['username', 'email', 'name', 'card_id', 'phone']
      const searchStrings = searchAttributes.map((it) => `${it}=${searchTerm}`)
      return searchStrings.join('&')
    },
    setPaginationTotals(metadata = null, dataLength = 0) {
      const currentPage = this.pagination.page || 1
      const size = this.pagination.size || 10
      const count = metadata?.count ?? dataLength ?? 0
      const lastPage = size ? Math.ceil((count || 0) / size) : 0
      const from = count ? (currentPage - 1) * size + 1 : 0
      const to = count ? Math.min(from + size - 1, count) : 0
      this.pagination.from = from
      this.pagination.to = to
      this.pagination.page = currentPage
      this.pagination.last_page = lastPage
      this.pagination.count = count
    },
    async fetchTransactions() {
      if (!this.arenaId) {
        this.resetState()
        return
      }
      this.isLoadingTransactions = true
      try {
        const { page, size } = this.pagination
        const params = {
          page,
          size,
          sort: this.filters.sort,
        }
        if (this.filters.created_gte) {
          params.created_gte = `${this.filters.created_gte}T00:00:00`
        }
        if (this.filters.created_lt) {
          const next = new Date(this.filters.created_lt)
          next.setDate(next.getDate() + 1)
          const y = next.getFullYear()
          const m = String(next.getMonth() + 1).padStart(2, '0')
          const d = String(next.getDate()).padStart(2, '0')
          params.created_lt = `${y}-${m}-${d}T00:00:00`
        }
        if (this.filters.buyer_id) {
          params.buyer_id = String(this.filters.buyer_id)
        }
        if (this.filters.seller_id) {
          params.seller_id = String(this.filters.seller_id)
        }
        const response = await Transaction.api.list(this.arenaId, params)
        const payload = this.unpackResponse(response)
        this.transactions = payload.map((dto) => new Transaction(dto))
        const metadata = response?.data?.metadata || {}
        this.setPaginationTotals(metadata, this.transactions.length)
        await this.fetchUsersForTransactions()
      } catch (error) {
        console.error('Failed to fetch transactions.', error)
        this.resetState()
      } finally {
        this.isLoadingTransactions = false
      }
    },
    async fetchUsersForTransactions() {
      const userIds = [
        ...new Set([
          ...this.transactions
            .map((transaction) => transaction.seller_user_id)
            .filter((id) => id && id !== 0),
          ...this.transactions
            .map((transaction) => transaction.buyer_user_id)
            .filter((id) => id && id !== 0),
        ]),
      ]

      if (userIds.length === 0) {
        this.usersById = {}
        return
      }

      try {
        const userPromises = []
        for (let i = 0; i < userIds.length; i += 100) {
          const chunk = userIds.slice(i, i + 100)
          userPromises.push(User.api.findByIds(chunk))
        }

        const usersResponses = await Promise.all(userPromises)
        const users = usersResponses.flatMap((response) => response.data || [])

        this.usersById = users.reduce((acc, user) => {
          acc[user.id] = user
          return acc
        }, {})
      } catch (error) {
        console.error('Failed to fetch users for transactions.', error)
        this.usersById = {}
      }
    },
    refresh() {
      this.pagination.page = 1
      this.fetchTransactions()
    },
    onFirstPage() {
      return this.pagination.page === 1
    },
    setPageToOne() {
      this.pagination.page = 1
    },
    unpackResponse(response) {
      const payload = response?.data?.data ?? response?.data ?? []
      if (Array.isArray(payload)) return payload
      if (Array.isArray(payload?.data)) return payload.data
      return []
    },
  },
  watch: {
    arenaId(newArenaId, oldArenaId) {
      if (newArenaId === oldArenaId) return
      if (!newArenaId) {
        this.resetState()
        return
      }
      this.resetState()
      this.fetchTransactions()
    },
    'pagination.size'(newVal, oldVal) {
      if (newVal === oldVal) return
      if (this.onFirstPage()) {
        this.fetchTransactions()
      } else {
        this.setPageToOne()
      }
    },
    'pagination.page'(newVal, oldVal) {
      if (newVal === oldVal) return
      this.fetchTransactions()
    },
    'filters.sort'() {
      this.pagination.page = 1
      this.fetchTransactions()
    },
    'filters.created_gte'() {
      this.pagination.page = 1
      this.fetchTransactions()
    },
    'filters.created_lt'() {
      this.pagination.page = 1
      this.fetchTransactions()
    },
    'filters.buyer_id'() {
      this.pagination.page = 1
      this.fetchTransactions()
    },
    'filters.seller_id'() {
      this.pagination.page = 1
      this.fetchTransactions()
    },
    buyerSearch(newVal) {
      if (this.buyerSearchDebounce) {
        clearTimeout(this.buyerSearchDebounce)
      }
      if (newVal && newVal.trim()) {
        this.buyerSearchDebounce = setTimeout(() => {
          this.fetchBuyerUsers(newVal)
        }, 400)
      } else {
        this.buyerUsers = []
      }
    },
    sellerSearch(newVal) {
      if (this.sellerSearchDebounce) {
        clearTimeout(this.sellerSearchDebounce)
      }
      if (newVal && newVal.trim()) {
        this.sellerSearchDebounce = setTimeout(() => {
          this.fetchSellerUsers(newVal)
        }, 400)
      } else {
        this.sellerUsers = []
      }
    },
  },
}
</script>

<style scoped>
.vertical-divider-small {
  height: 24px !important;
  align-self: center;
}
</style>
